/**
 * v4.12.0 — Bulk Command Strip.
 *
 * Sticky bottom-of-viewport ribbon that appears when ≥1 card is selected.
 * Designed as a "command strip" (elevated, contextual) — not a toolbar.
 * Each action opens its own configurator modal which in turn surfaces
 * the preview-and-confirm flow.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    CalendarClock, Compass, Shield, Activity, UserSquare, X,
} from "lucide-react";

const ActionBtn = ({ icon: Icon, label, onClick, testId }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-200 hover:text-white border transition-all"
        style={{
            background: "rgba(15, 23, 42, 0.55)",
            borderColor: "rgba(148, 163, 184, 0.20)",
        }}
    >
        <Icon size={14} strokeWidth={1.85} className="text-sky-300" />
        {label}
    </button>
);

export default function BulkCommandStrip({
    count, onClear,
    onOpenSchedule, onOpenRouting, onOpenMode, onOpenStatus, onOpenAssignment,
}) {
    const { t } = useTranslation();
    if (!count) return null;
    return (
        <div
            data-testid="bulk-command-strip"
            data-insight-id="fleet.bulk.command-strip"
            data-insight-label="Bulk command strip"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]"
        >
            <div
                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 rounded-2xl border backdrop-blur"
                style={{
                    background: "rgba(8, 14, 28, 0.85)",
                    borderColor: "rgba(56, 189, 248, 0.35)",
                    boxShadow: "0 24px 60px -20px rgba(8, 47, 73, 0.85), 0 0 0 1px rgba(56,189,248,0.08) inset",
                }}
            >
                <div className="flex items-center gap-2 pr-2 sm:pr-3 border-r border-slate-500/20">
                    <span className="text-[10px] uppercase tracking-wider text-sky-300/80 hidden sm:inline">
                        {t("card_fleet.bulk.command_strip_eyebrow")}
                    </span>
                    <span
                        className="text-sm font-semibold text-slate-100"
                        data-testid="strip-count"
                    >
                        {t("card_fleet.bulk.n_selected", { n: count })}
                    </span>
                </div>
                <ActionBtn icon={CalendarClock} label={t("card_fleet.bulk.action_schedule")} onClick={onOpenSchedule} testId="bulk-action-schedule" />
                <ActionBtn icon={Compass} label={t("card_fleet.bulk.action_routing")} onClick={onOpenRouting} testId="bulk-action-routing" />
                <ActionBtn icon={Shield} label={t("card_fleet.bulk.action_mode")} onClick={onOpenMode} testId="bulk-action-mode" />
                <ActionBtn icon={Activity} label={t("card_fleet.bulk.action_status")} onClick={onOpenStatus} testId="bulk-action-status" />
                <ActionBtn icon={UserSquare} label={t("card_fleet.bulk.action_assign")} onClick={onOpenAssignment} testId="bulk-action-assign" />
                <button
                    type="button"
                    onClick={onClear}
                    data-testid="bulk-strip-clear"
                    className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                    aria-label={t("card_fleet.bulk.clear_selection")}
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
